import React, { useState, useEffect } from 'react'
import { Info } from 'lucide-react'
import { useEnergyStore } from '@/stores/useEnergyStore'

interface SliderProps {
  label: string
  value: number
  unit: string
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  darkMode?: boolean
  helpKey?: string
}

export const Slider: React.FC<SliderProps> = ({
  label,
  value,
  unit,
  min,
  max,
  step,
  onChange,
  darkMode = true,
  helpKey,
}) => {
  const openHelpToItem = useEnergyStore((s) => s.openHelpToItem)
  const safeValue = value ?? min ?? 0
  const [displayValue, setDisplayValue] = useState(safeValue.toString())

  useEffect(() => {
    const safeVal = value ?? min ?? 0
    const parsed = parseFloat(displayValue)
    
    // Don't overwrite temporary blank/empty/minus/dot inputs if they match the fallback value
    if (displayValue === '' || displayValue === '-' || displayValue === '.' || displayValue === '-.') {
      const fallbackVal = min ?? 0
      if (safeVal === fallbackVal) {
        return
      }
    }

    if (isNaN(parsed) || parsed !== safeVal) {
      setDisplayValue(safeVal.toString())
    }
  }, [value, min])

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value
    setDisplayValue(rawVal)

    if (rawVal === '') {
      onChange(min ?? 0)
      return
    }

    const cleanVal = rawVal.replace(/[^-0-9.]/g, '')
    if (cleanVal.endsWith('.')) return

    const val = parseFloat(cleanVal)
    if (!isNaN(val)) onChange(val)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1">
          {helpKey && (
            <button
              onClick={() => openHelpToItem(helpKey)}
              className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
              }`}
              title={`Help with ${label}`}
            >
              <Info size={10} />
            </button>
          )}
          <label
            className={`text-[10px] font-black uppercase tracking-wider leading-tight ${
              darkMode ? 'text-slate-500' : 'text-slate-400'
            }`}
          >
            {label}
          </label>
        </div>
        <div
          className={`flex items-center rounded-xl px-2 py-1 border transition-all duration-300 ${
            darkMode
              ? 'bg-white/5 border-white/5 focus-within:border-emerald-500/50 focus-within:bg-white/10'
              : 'bg-slate-100 border-slate-200 focus-within:border-emerald-500/50 focus-within:bg-white'
          }`}
        >
          <input
            type="text"
            inputMode="decimal"
            value={displayValue}
            onFocus={(e) => e.target.select()}
            onChange={handleTextChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur()
              }
            }}
            onBlur={() => setDisplayValue(safeValue.toString())}
            className={`bg-transparent text-[11px] font-bold outline-none w-16 text-right ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}
          />
          <span
            className={`text-[9px] font-black ml-1 uppercase opacity-40 ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}
          >
            {unit}
          </span>
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={safeValue}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className={`w-full h-1.5 rounded-full appearance-none cursor-pointer transition-all ${
          darkMode
            ? 'bg-white/10 accent-emerald-500 hover:accent-emerald-400'
            : 'bg-slate-200 accent-emerald-600 hover:accent-emerald-500'
        }`}
      />
    </div>
  )
}
